import './index.css';
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import AccountMenu from "../AccountMenu";
import useUser from "../../hooks/useUser";
import { useEffect, useState } from "react";
import ProjectSelector from "./projectSelector";
import {
  AngleDownOutline, ElementsListingIcon, SitesListingIcon, SettingsIcon, TicketsListingIcon,
  PersonIcon, UsersSettingsIcon, UserIcon, CompaniesIcon, EntitiesIcon, HamburgerMenu,
  AreasIcon, DoubleArrowIcon, GraphIcon, DashboardIcon, EntitiesIconOutline, ProjectIcon
} from "../common/Icons";
import { Link } from 'react-router-dom';


const BKG_COLOR = '#2a3d2b';
const DIVIDER_COLOR = '#8dca89'

const sectionSelectedStyle = {
  boxShadow: 'inset 4px 0px 0 0 #24cf24',
  backgroundColor: '#234923',
}

const drawerPaperBase = {
  transition: 'width 0.4s ease',
  backgroundColor: BKG_COLOR,
  color: '#fef9f9',
  boxSizing: "border-box",
  overflow: 'visible',
};

interface TopSideBarProps {
  currentSection: string
  hideMenu?: boolean
  hideUser?: boolean
  hideProjectSelector?: boolean
  disableProjectSelector?: boolean
}

const TopSideBar: React.FC<TopSideBarProps> = ({
  currentSection,
  hideMenu,
  hideUser,
  hideProjectSelector,
  disableProjectSelector }) => {

  const [openSideBar, setOpenSideBar] = useState(false)
  const sideBarWidth = { 'open': '250px', 'closed': '35px' }
  const [section, setSection] = useState(currentSection)

  const sideMenuTree: { [key: string]: string | null } = {
    'dashboard': null,
    'tickets': null,
    'elements': null,
    'links': null,
    'administration': null,
    'companies': null,
    'areas': null,
    'operations-reports': null,
    'ticket-entities': 'administration',
    'element-entities': 'administration',
    'persons': 'users-management',
    'users': 'users-management'
  }

  const [sectionExpanded, setSectionExpanded] = useState<string | null>(
    sideMenuTree[currentSection]
  )

  const { user, userAdmin }: any = useUser()

  useEffect(() => {
    setSection(currentSection)
    setSectionExpanded(sideMenuTree[currentSection])
  }, [currentSection])

  const handleToggleSideMenu = () => {
    setOpenSideBar(!openSideBar)
  };

  const handleSectionClick = (section: string) => {
    setSectionExpanded(sectionExpanded === section ? null : section);
  };

  return (user) && (
    <div className="top-side-bar">

      <div className="top-bar">
        <div className="item top-bar__menu">
          {(true) ? '' : (
            <HamburgerMenu
              className="top-bar__menu-icon"
              aria-label="open drawer"
              onClick={handleToggleSideMenu}
            />
          )}

          <div className="top-bar__logotype">
            <span className="top-bar__logo"></span>
            <span className="top-bar__brand"></span>
          </div>
        </div>

        {(!hideProjectSelector) && (
          <div className='item top-bar__projectselector'>
            <ProjectSelector onlyRead={disableProjectSelector} />
          </div>
        )}

        <div className='item top-bar__accountmenu'>
          {(hideUser) ? '' : (
            <span className="account-menu-icon">
              <AccountMenu />
            </span>
          )}
        </div>
      </div>

      <Drawer
        sx={{
          flexShrink: 0,
          zIndex: 9999,
          "& .MuiDrawer-paper": {
            ...drawerPaperBase,
            width: openSideBar ? sideBarWidth.open : sideBarWidth.closed,
            visibility: hideMenu ? 'hidden' : 'auto'
          }
        }}
        hideBackdrop={!openSideBar}
        variant="permanent"
        anchor="left"
        open={true}
      >
        <nav className="topsidebar-arrow-section">
          <IconButton sx={{ color: '#8dca89', fontSize: '1rem' }} onClick={handleToggleSideMenu}>
            <DoubleArrowIcon className={`topsidebar-arrow-icon ${!openSideBar ? 'closed' : ''}`} />
          </IconButton>
        </nav>

        <List dense={false}>

          <ListItem key="dashboard" disablePadding className="item-section"
            sx={section === 'dashboard' ? sectionSelectedStyle : {}}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'/dashboard'}>
                <DashboardIcon className='item-icon dashboard' />
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Dashboard</div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Dashboard</span>
              </div>
            )}
          </ListItem>

          <Divider sx={{ backgroundColor: DIVIDER_COLOR }} />

          <ListItem key="tickets" disablePadding className="item-section"
            sx={section === 'tickets' ? sectionSelectedStyle : {}}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'/tickets/listing'}>
                {<TicketsListingIcon className='item-icon' />}
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Tickets</div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Tickets</span>
              </div>
            )}
          </ListItem>

          <ListItem key="elements" disablePadding className="item-section"
            sx={section === 'elements' ? sectionSelectedStyle : {}}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'/elements/listing'}>
                <ElementsListingIcon className='item-icon' />
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Elementos</div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Elementos</span>
              </div>
            )}
          </ListItem>

          <ListItem key="sites" disablePadding className="item-section"
            sx={section === 'sites' ? sectionSelectedStyle : {}}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'/sites/listing'}>
                <SitesListingIcon className='item-icon' />
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Sitios</div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Sitios</span>
              </div>
            )}
          </ListItem>

          <ListItem key="companies" disablePadding className="item-section"
            sx={section === 'companies' ? sectionSelectedStyle : {}}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'/companies/listing'}>
                <CompaniesIcon className='item-icon' />
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Compañías</div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Compañías</span>
              </div>
            )}
          </ListItem>

          <ListItem key="areas" disablePadding className="item-section"
            sx={section === 'areas' ? sectionSelectedStyle : {}}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'/areas/listing'}>
                <AreasIcon className='item-icon' />
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Áreas</div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Áreas</span>
              </div>
            )}
          </ListItem>

          <ListItem key="operations-reports" disablePadding className="item-section"
            sx={section === 'operations-reports' ? sectionSelectedStyle : {}}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'/operations-reports'}>
                <GraphIcon className='item-icon' />
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Reportes de Operaciones</div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Reportes de Operaciones</span>
              </div>
            )}
          </ListItem>

        </List>

        <Divider sx={{ backgroundColor: DIVIDER_COLOR }} />

        <List dense={true}>

          <ListItem key="administration" disablePadding className="item-section"
            onClick={() => { handleSectionClick('administration') }}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'#'}>
                {<SettingsIcon className='item-icon' />}
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Administración
                  <div className={`down-up-arrow-subsection ${sectionExpanded === 'administration' ? 'subsection-opened' : ''}`}>
                    <AngleDownOutline />
                  </div>
                </div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Administración</span>
              </div>
            )}
          </ListItem>

          <div className={`${sectionExpanded === 'administration' ? 'submenu-expanded' : 'submenu-collapsed'}`}>

            <ListItem key="ticket-entities" disablePadding className="item-section"
              sx={section === 'ticket-entities' ? sectionSelectedStyle : {}}>
              <nav className='item-content'>
                <Link className='item-content-link' to={'/settings/ticket-entities'}>
                  {<EntitiesIcon className='item-icon subtitle' />}
                  <div className={`item-title subtitle ${openSideBar ? 'show' : ''}`}>Entidades de Ticket</div>
                </Link>
              </nav>
              {!openSideBar && (
                <div className='itemsidebar-tooltip show'>
                  <span>Entidades de Ticket</span>
                </div>
              )}
            </ListItem>

            <ListItem key="element-entities" disablePadding className="item-section"
              sx={section === 'element-entities' ? sectionSelectedStyle : {}}>
              <nav className='item-content'>
                <Link className='item-content-link' to={'/settings/element-entities'}>
                  {<EntitiesIconOutline className='item-icon subtitle' />}
                  <div className={`item-title subtitle ${openSideBar ? 'show' : ''}`}>Entidades de Elemento</div>
                </Link>
              </nav>
              {!openSideBar && (
                <div className='itemsidebar-tooltip show'>
                  <span>Entidades de Elemento</span>
                </div>
              )}
            </ListItem>

            {userAdmin && (
              <ListItem key="project-settings" disablePadding className="item-section"
                sx={section === 'project-settings' ? sectionSelectedStyle : {}}>

                <nav className='item-content'>
                  <Link className='item-content-link' to={'/settings/project-settings'}>
                    <ProjectIcon className='item-icon subtitle' />
                    <div className={`item-title subtitle ${openSideBar ? 'show' : ''}`}>
                      Proyecto
                    </div>
                  </Link>
                </nav>

                {!openSideBar && (
                  <div className='itemsidebar-tooltip show'>
                    <span>Proyecto</span>
                  </div>
                )}

              </ListItem>
            )}

          </div>

          <ListItem key="users-management" disablePadding className="item-section"
            onClick={() => { handleSectionClick('users-management') }}>
            <nav className='item-content'>
              <Link className='item-content-link' to={'#'}>
                {<UsersSettingsIcon className='item-icon' />}
                <div className={`item-title ${openSideBar ? 'show' : ''}`}>Gestión de Usuarios
                  <div className={`down-up-arrow-subsection ${sectionExpanded === 'users-management' ? 'subsection-opened' : ''}`}>
                    <AngleDownOutline />
                  </div>
                </div>
              </Link>
            </nav>
            {!openSideBar && (
              <div className='itemsidebar-tooltip show'>
                <span>Gestión de Usuarios</span>
              </div>
            )}
          </ListItem>

          <div className={`${sectionExpanded === 'users-management' ? 'submenu-expanded' : 'submenu-collapsed'}`}>

            <ListItem key="persons" disablePadding className="item-section"
              sx={section === 'persons' ? sectionSelectedStyle : {}}>
              <nav className='item-content'>
                <Link className='item-content-link' to={'/users-management/persons'}>
                  {<PersonIcon className='item-icon subtitle' />}
                  <div className={`item-title subtitle ${openSideBar ? 'show' : ''}`}>Personas</div>
                </Link>
              </nav>
              {!openSideBar && (
                <div className='itemsidebar-tooltip show'>
                  <span>Personas</span>
                </div>
              )}
            </ListItem>

            <ListItem key="users" disablePadding className="item-section"
              sx={section === 'users' ? sectionSelectedStyle : {}}>
              <nav className='item-content'>
                <Link className='item-content-link' to={'/users-management/users'}>
                  {<UserIcon className='item-icon subtitle' />}
                  <div className={`item-title subtitle ${openSideBar ? 'show' : ''}`}>Usuarios</div>
                </Link>
              </nav>
              {!openSideBar && (
                <div className='itemsidebar-tooltip show'>
                  <span>Usuarios</span>
                </div>
              )}
            </ListItem>

          </div>
        </List>

      </Drawer>
    </div>
  )
}

export default TopSideBar;